/**
 * The card field at the desk.
 *
 *   "my type of clients just want to hand me the card"
 *   "It does not let me manually enter a card let me show you blvd"
 *
 * The number is typed into a field the PROCESSOR owns, inside this page, and
 * goes straight from the browser to them. Nothing of the card ever reaches
 * our server or this code: what comes back is a token good for one card,
 * which is what the till charges. That is the same arrangement as the hosted
 * page she was being sent to, without leaving the till.
 *
 * Loaded only when she asks for it, so a desk that never types a card never
 * fetches any of this.
 */

interface StripeCardElement {
  mount(target: HTMLElement): void;
  unmount(): void;
  destroy(): void;
  on(event: string, handler: (e: { error?: { message?: string }; complete?: boolean }) => void): void;
}
interface StripeLike {
  elements(opts?: Record<string, unknown>): {
    create(type: "card", opts?: Record<string, unknown>): StripeCardElement;
  };
  createPaymentMethod(opts: {
    type: "card";
    card: StripeCardElement;
    billing_details?: { name?: string | null; email?: string | null };
  }): Promise<{ paymentMethod?: { id: string }; error?: { message?: string } }>;
}

declare global {
  interface Window { Stripe?: (key: string) => StripeLike }
}

const SRC = "https://js.stripe.com/v3/";

/** The same ink, quieter - for the "1234 1234 …" prompt. */
function faded(colour: string): string {
  const m = colour.match(/rgba?\(([^)]+)\)/);
  if (!m) return "#9ca3af";
  const [r, g, b] = m[1].split(",").map((n) => Number(n.trim()));
  if ([r, g, b].some((n) => !Number.isFinite(n))) return "#9ca3af";
  return `rgba(${r}, ${g}, ${b}, 0.45)`;
}
let loading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => { loading = null; reject(new Error("The card field could not be loaded.")); };
    document.head.appendChild(el);
  });
  return loading;
}

/** One card field, mounted where you put it. Dark to match the till. */
export async function mountCardField(key: string, target: HTMLElement): Promise<{
  element: StripeCardElement;
  stripe: StripeLike;
}> {
  await loadScript();
  const make = window.Stripe;
  if (!make) throw new Error("The card field could not be loaded.");
  const stripe = make(key);
  const elements = stripe.elements();
  /**
   * The field is in an iframe, so it inherits nothing from this page - every
   * colour has to be handed to it. Taken from the box it is being put in, so
   * it follows the practice's own light or dark theme instead of guessing:
   * light text in a light box is a card nobody can read as they type it.
   */
  const seen = window.getComputedStyle(target);
  const ink = seen.color || "#18181b";
  const element = elements.create("card", {
    hidePostalCode: false,
    style: {
      base: {
        color: ink,
        fontFamily: seen.fontFamily || "inherit",
        fontSize: "16px",
        "::placeholder": { color: faded(ink) },
      },
      invalid: { color: "#e11d48", iconColor: "#e11d48" },
    },
  });
  element.mount(target);
  return { element, stripe };
}

/** Turn what she typed into a token for this one card. */
export async function tokeniseCard(
  stripe: StripeLike, element: StripeCardElement, name: string | null,
): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
  const r = await stripe.createPaymentMethod({
    type: "card",
    card: element,
    ...(name ? { billing_details: { name } } : {}),
  });
  if (r.error || !r.paymentMethod) {
    return { ok: false, error: r.error?.message ?? "That card could not be read." };
  }
  return { ok: true, id: r.paymentMethod.id };
}
